package fyi.moko.hotpl.ui.photo

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.data.image.ImageStoreFullException
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.usecase.CorrectPhotoUseCase
import fyi.moko.hotpl.domain.usecase.ObservePlaceUseCase
import fyi.moko.hotpl.domain.usecase.ObserveVisitsUseCase
import fyi.moko.hotpl.domain.usecase.RotatePhotoUseCase
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch

/**
 * 뷰어가 어느 목록을 넘기고 있는지. 방문 사진과 가게 사진은 **할 수 있는 일이 다르다**
 * — 방문 사진만 가게로 옮길 수 있고, 가게 사진만 대표가 될 수 있다
 * ([CorrectPhotoUseCase] 참고).
 */
sealed interface PhotoViewerSource {
    val placeId: String

    data class PlacePhotos(override val placeId: String) : PhotoViewerSource
    data class VisitPhotos(override val placeId: String, val visitId: String) : PhotoViewerSource
}

/**
 * Backs the full-screen photo viewer.
 *
 * 사진을 **파일명으로** 들고 있다 — 비트맵을 상세 화면에서 넘겨받지 않는다. 회전(R-4)이
 * 저장된 픽셀을 갈아끼우고 새 이름을 돌려주므로, 여기서 필요한 것이 바로 그 이름이다.
 *
 * 목록은 계속 흐름을 따라간다. 시트에서 사진을 지우면 원본 목록이 줄고, 그러면 보고 있던
 * 자리를 클램프한다 — 마지막 장을 지웠으면 [closed]로 화면을 닫는다.
 */
class PhotoViewerViewModel(
    private val observePlace: ObservePlaceUseCase,
    private val observeVisits: ObserveVisitsUseCase,
    private val correctPhoto: CorrectPhotoUseCase,
    private val rotatePhoto: RotatePhotoUseCase,
) : ViewModel() {

    var pages by mutableStateOf<List<String>>(emptyList())
        private set
    var index by mutableStateOf(0)
        private set
    /** True once there is nothing left to look at — the screen pops itself. */
    var closed by mutableStateOf(false)
        private set
    var actionsOpen by mutableStateOf(false)
        private set
    var placeName by mutableStateOf("")
        private set
    /** True while a turn is being written. The button is held down so two taps can't race. */
    var rotating by mutableStateOf(false)
        private set
    /** One-shot text for the rare failure — read once and cleared ([clearMessage]). */
    var message by mutableStateOf<String?>(null)
        private set

    private var source: PhotoViewerSource? = null
    private var coverFileName: String? = null
    private var started = false

    val currentFileName: String? get() = pages.getOrNull(index)

    /** [startFileName] is the photo that was tapped; paging starts there. */
    fun start(source: PhotoViewerSource, startFileName: String?) {
        if (started) return
        started = true
        this.source = source
        viewModelScope.launch {
            combine(
                observePlace(source.placeId),
                observeVisits(source.placeId),
            ) { place, visits ->
                placeName = place?.name.orEmpty()
                coverFileName = place?.imageFileName
                when (source) {
                    is PhotoViewerSource.PlacePhotos -> place?.let(::placePhotoOrder).orEmpty()
                    is PhotoViewerSource.VisitPhotos ->
                        visits.firstOrNull { it.id == source.visitId }
                            ?.photos.orEmpty().map { it.fileName }
                }
            }.collect { fileNames -> onPagesChanged(fileNames, startFileName) }
        }
    }

    private fun onPagesChanged(fileNames: List<String>, startFileName: String?) {
        val firstLoad = pages.isEmpty()
        pages = fileNames
        if (fileNames.isEmpty()) {
            closed = true
            return
        }
        index = when {
            firstLoad -> fileNames.indexOf(startFileName).takeIf { it >= 0 } ?: 0
            else -> index.coerceIn(0, fileNames.lastIndex)
        }
    }

    fun onPageChanged(page: Int) {
        if (page in pages.indices) index = page
    }

    fun openActions() { actionsOpen = true }
    fun dismissActions() { actionsOpen = false }

    /** Only a place photo that isn't already the cover has somewhere to go. */
    val canSetAsCover: Boolean
        get() = source is PhotoViewerSource.PlacePhotos &&
            currentFileName != null &&
            currentFileName != coverFileName

    val canPromoteToPlace: Boolean get() = source is PhotoViewerSource.VisitPhotos

    fun setAsCover() {
        val fileName = currentFileName ?: return
        val src = source as? PhotoViewerSource.PlacePhotos ?: return
        actionsOpen = false
        viewModelScope.launch { correctPhoto.setAsCover(placeId = src.placeId, fileName = fileName) }
    }

    fun promoteToPlace() {
        val fileName = currentFileName ?: return
        val src = source as? PhotoViewerSource.VisitPhotos ?: return
        actionsOpen = false
        viewModelScope.launch {
            correctPhoto.promoteToPlace(placeId = src.placeId, visitId = src.visitId, fileName = fileName)
        }
    }

    /**
     * Turns the photo on screen a quarter turn clockwise, for good — the bytes are rewritten
     * and the new file syncs like any other photo ([RotatePhotoUseCase]).
     *
     * 파일명이 바뀌므로 목록이 흐름을 타고 다시 내려오고, 그때 **자리는 그대로**다
     * (use case가 같은 위치를 갈아끼운다). 보고 있던 장이 안 튀는 이유가 그것이다.
     */
    fun rotateCurrent() {
        val fileName = currentFileName ?: return
        val src = source ?: return
        if (rotating) return
        actionsOpen = false
        rotating = true
        viewModelScope.launch {
            try {
                val rotated = when (src) {
                    is PhotoViewerSource.PlacePhotos ->
                        rotatePhoto.rotatePlacePhoto(placeId = src.placeId, fileName = fileName)
                    is PhotoViewerSource.VisitPhotos ->
                        rotatePhoto.rotateVisitPhoto(visitId = src.visitId, fileName = fileName)
                }
                if (rotated == null) message = "Could not turn this photo"
            } catch (e: ImageStoreFullException) {
                message = e.message ?: "Storage is full, the photo was not turned"
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                message = "Could not turn this photo"
            } finally {
                rotating = false
            }
        }
    }

    fun clearMessage() { message = null }

    fun removeCurrent() {
        val fileName = currentFileName ?: return
        val src = source ?: return
        actionsOpen = false
        viewModelScope.launch {
            when (src) {
                is PhotoViewerSource.PlacePhotos ->
                    correctPhoto.removeFromPlace(placeId = src.placeId, fileName = fileName)
                is PhotoViewerSource.VisitPhotos ->
                    correctPhoto.removeFromVisit(visitId = src.visitId, fileName = fileName)
            }
        }
    }
}

/**
 * 장소 사진의 페이지 순서: 대표 먼저. 헤더가 넘기던 순서와 같게 두는 편이, 갤러리 격자
 * 순서를 따라가는 것보다 헷갈리지 않는다 — 어차피 탭한 장에서 열린다.
 */
internal fun placePhotoOrder(place: Place): List<String> {
    val all = place.photos.map { it.fileName }.filter { it.isNotBlank() }.distinct()
    val cover = place.imageFileName?.takeIf { it.isNotBlank() }
    return when {
        all.isEmpty() -> listOfNotNull(cover)
        cover == null || cover !in all -> all
        else -> listOf(cover) + all.filterNot { it == cover }
    }
}
